import React, { useState } from 'react';
import { Button, Tooltip } from 'antd';
import classNames from 'classnames';
import {
  DocumentDownloadIcon,
  Share2Icon,
  TaskIcon,
  WarningIcon
} from 'icons';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { blokTestConst } from 'modules/blok-test';
import { fireConfetti, globalConst, isBrowser } from 'modules/global';
import { useMyResult } from 'modules/mavzuli-test';
import { useDownloadCertificate } from 'modules/private-universities';

import { ApplicationModal, Modal, Segmented, Visible } from 'components';

import RateModal from './rate-modal';
import ShareModal from './share-modal';

interface IProps {
  onClose: () => void;
  open: boolean;
  ticketId?: number;
}

const ResultsModal: React.FC<IProps> = ({ onClose, open, ticketId }) => {
  const t = useTranslations();
  const { replace } = useRouter();
  const { myResults, isLoading } = useMyResult(ticketId);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [openRate, setOpenRate] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [openApplicationCreate, setOpenApplicationCreate] = useState(false);
  const { mutate: downloadCertificate, isLoading: isDownloading } = useDownloadCertificate();

  const options =
    myResults?.result?.map((item, index) => ({
      label: item.name,
      value: index.toString()
    })) ?? [];

  const handleChange = (value: string) => {
    setSelectedIndex(parseInt(value, 10));
  };

  const totalQuestions = myResults?.result?.reduce((acc, curr) => acc + curr.questions.length, 0) || 0;
  const incorrectAnswers = myResults?.result.reduce((acc, result) => {
    const incorrectCount = result.questions.filter(
      ({ checked, correct }) => checked !== correct && checked !== 0
    ).length;

    return acc + incorrectCount;
  }, 0);

  const correctAnswersCount = myResults?.result?.reduce(
    (totalCorrect, res) =>
      totalCorrect +
      res.questions.reduce((count, question) => count + (question.checked === question.correct ? 1 : 0), 0),
    0
  );

  const currentTest = myResults?.result?.[selectedIndex];

  const getTooltipText = (checked?: number, correct?: number) => {
    if (checked === correct) return t('togri_javob');
    if (checked === 0) return t('belgilanmagan');
    return t('notogri_javob');
  };

  if (
    myResults?.type === blokTestConst.testTypes.TOPIC_TEST &&
    totalQuestions > 0 &&
    (correctAnswersCount ?? 0) / totalQuestions > 0.5
  ) {
    fireConfetti();
  }

  const onReloadWindow = () => {
    if (isBrowser()) {
      window.location.reload();
    }
  };

  return (
    <>
      <Modal
        loading={isLoading}
        footer={
          <div className="mt-5">
            {myResults?.mine && myResults?.can_generate_certificate && (
              <Button
                loading={isDownloading}
                onClick={() => downloadCertificate(ticketId)}
                className="!w-full !rounded-lg !border !px-3 !py-5 !font-medium !text-indigo-600 hover:!border-indigo-600"
              >
                <DocumentDownloadIcon />
                {t('sertifikatni_korish')}
              </Button>
            )}
            <div className="mt-2 flex items-center gap-2">
              <Visible
                if={myResults?.type === blokTestConst.testTypes.TOPIC_TEST}
                else={
                  <Visible
                    if={myResults?.type === blokTestConst.testTypes.UNIVERSITY_TEST}
                    else={
                      <>
                        <Button
                          onClick={() => replace(`/${myResults?.type}`)}
                          className="!w-30 !rounded-lg !border-none !bg-gray-100 !px-3 !py-5 hover:!border-none hover:!text-black "
                        >
                          <span className="block w-full overflow-hidden truncate"> {t('auth.last_step_button')}</span>
                        </Button>
                        <Link href={`${globalConst.pageNames.UNIVER_REC}/${ticketId}`} className="w-full">
                          <Button className="!w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none">
                            {t('tavsiya_etilgan_oliygohlar')}
                          </Button>
                        </Link>
                      </>
                    }
                  >
                    <Visible
                      if={myResults?.application}
                      else={
                        <>
                          <Button
                            onClick={() => replace(globalConst.pageNames.PRIVATE_UNIVERSITIES)}
                            className="!w-30 !rounded-lg !border-none !bg-gray-100 !px-3 !py-5 hover:!border-none hover:!text-black "
                          >
                            <span className="block w-full overflow-hidden truncate"> {t('auth.last_step_button')}</span>
                          </Button>
                          <Button
                            onClick={() => setOpenApplicationCreate(true)}
                            className="!w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none"
                          >
                            {t('ArizaQoldirish')}
                          </Button>
                        </>
                      }
                    >
                      <Button
                        onClick={() => replace(globalConst.pageNames.PRIVATE_UNIVERSITIES)}
                        className="!w-30 !rounded-lg !border-none !bg-gray-100 !px-3 !py-5 hover:!border-none hover:!text-black "
                      >
                        <span className="block w-full overflow-hidden truncate"> {t('auth.last_step_button')}</span>
                      </Button>
                      <Button
                        onClick={onReloadWindow}
                        className="!w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none"
                      >
                        {t('qayta_ishlash')}
                      </Button>
                    </Visible>
                  </Visible>
                }
              >
                <Link href={globalConst.pageNames.TOPIC_TEST}>
                  <Button className="!w-30 !rounded-lg !border-none !bg-gray-100 !px-3 !py-5 hover:!border-none hover:!text-black ">
                    <span className="block w-full overflow-hidden truncate"> {t('auth.last_step_button')}</span>
                  </Button>
                </Link>
                <Button
                  onClick={onReloadWindow}
                  className="!w-full !rounded-lg !border-none !bg-green-500 !px-7 !py-5 !text-white hover:!border-none"
                >
                  {t('qayta_ishlash')}
                </Button>
              </Visible>
            </div>
          </div>
        }
        open={open}
        centered
        height="100%"
        onClose={onClose}
        closeIcon={false}
        width="400px"
        styles={{
          content: { padding: '16px', borderRadius: '12px' }
        }}
        title={<span className="text-lg">{t('yakuniy_natijalar')}</span>}
        maskClosable={false}
        keyboard={false}
        closable={false}
      >
        <div className="flex justify-center">
          <Visible
            if={myResults?.type === blokTestConst.testTypes.UNIVERSITY_TEST}
            else={
              <Image
                src="/images/happy-result.png"
                alt="happy-result222"
                width={140}
                height={100}
                loading="lazy"
                className="drop-shadow-[0_2px_50px_rgba(255,255,0,0.4)]"
                title="Yaxshi ball to'plagan foydalanuvchiga ko'rinadigon rasm"
              />
            }
          >
            <Visible
              if={myResults?.ball >= (myResults?.direction?.passing_ball || 0)}
              else={
                <Image
                  src="/images/bad-result.png"
                  alt="bad-result"
                  width={140}
                  height={100}
                  loading="lazy"
                  className="drop-shadow-[0_2px_50px_rgba(255,255,0,0.4)]"
                  title="Yaxshi ball to'play olmagan foydalanuvchiga ko'rinadigon rasm"
                />
              }
            >
              <Image
                src="/images/happy-result.png"
                alt="happy-result"
                width={140}
                height={100}
                loading="lazy"
                className="drop-shadow-[0_2px_50px_rgba(255,255,0,0.4)]"
                title="Yaxshi ball to'plagan foydalanuvchiga ko'rinadigon rasm"
              />
            </Visible>
          </Visible>
        </div>
        <p className="my-4 text-center text-lg font-medium">{myResults?.user}</p>

        {myResults?.type === blokTestConst.testTypes.UNIVERSITY_TEST &&
          (myResults.ball >= myResults.direction.passing_ball ? (
            <div className="my-4 rounded-xl bg-green-50 p-3 text-center font-medium text-green-500">
              {t.rich('Tabriklaymiz_siz_otdingiz', {
                universityName: myResults?.direction?.university?.name ?? 'Universitet',
                b: chunks => <b>{chunks}</b>
              })}
            </div>
          ) : (
            <div className="my-4 rounded-xl  bg-red-50 p-3 text-center font-medium text-red-500">
              {t('Uzr_siz_otish_balini_yiga_olmadingiz')}
            </div>
          ))}

        <div className="flex items-center justify-between rounded-xl bg-gray-100 p-2 py-4">
          <div className="flex w-full flex-col space-y-1 border-r-2 border-gray-300 text-center">
            <div className="flex items-center justify-center gap-1">
              <div className="size-4.5 relative">
                <Image
                  src="/images/total-test-paper.webp"
                  fill
                  alt="Total tests count"
                  title="Umumiy savollar soni"
                />
              </div>
              <p className="font-medium">{totalQuestions}</p>
            </div>
            <p className="text-xs text-gray-400">{t('jami_test')}</p>
          </div>
          <div className="flex w-full flex-col space-y-1 border-r-2 border-gray-300 text-center">
            <div className="flex items-center justify-center gap-1">
              <div className="size-4.5 relative">
                <Image src="/images/correct-answer.webp" fill alt="Correct Answer" title="Tog'ri javob" />
              </div>
              <p className="font-medium">{correctAnswersCount}</p>
            </div>
            <p className="text-xs text-gray-400">{t('togrilar')}</p>
          </div>
          <div className="flex w-full flex-col space-y-1 text-center">
            <div className="flex items-center justify-center gap-1">
              <div className="size-4.5 relative">
                <Image
                  src="/images/incorrect-answer.webp"
                  fill
                  alt="Incorrect Answer"
                  title="Notog'ri javob"
                />
              </div>
              <p className="font-medium">{incorrectAnswers}</p>
            </div>
            <p className="text-xs text-gray-400">{t('xatolar')}</p>
          </div>
        </div>

        <Visible
          if={myResults?.type === blokTestConst.testTypes.TOPIC_TEST}
          else={
            <Visible
              if={
                myResults?.type === blokTestConst.testTypes.VIRTUAL_TEST ||
                myResults?.type === blokTestConst.testTypes.DTM_TEST
              }
              else={
                <div className="mt-3 flex items-center justify-between rounded-xl bg-gray-100 p-1">
                  <div className="flex w-full flex-col border-r-2 border-gray-300 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <p className="font-medium text-orange-400">{myResults?.direction?.passing_ball}</p>
                    </div>
                    <p className="text-xs text-gray-400">{t('otish_bali')}</p>
                  </div>

                  <div className="flex w-full flex-col text-center">
                    <div className="flex items-center justify-center gap-1">
                      <p className="font-medium text-red-700">{myResults?.ball}</p>
                    </div>
                    <p className="text-xs text-gray-400">{t('toplangan_bal')}</p>
                  </div>
                </div>
              }
            >
              <div className="mt-3 flex items-center justify-between rounded-xl bg-gray-100 p-1">
                <div className="flex w-full flex-col text-center">
                  <div className="flex items-center justify-center gap-1">
                    <p className="font-medium text-yellow-400">{myResults?.ball}</p>
                  </div>
                  <p className="text-xs text-gray-400">{t('toplangan_bal')}</p>
                </div>
              </div>
            </Visible>
          }
        >
          <div className="mt-3 rounded-xl bg-gray-100 p-3 font-medium">{currentTest?.lesson_name}</div>
        </Visible>

        <Segmented type={myResults?.type} options={options} onChange={handleChange} labelPrefix={t('Fan')} />

        {currentTest && (
          <div className="mt-3 grid grid-cols-8 gap-2 md:grid-cols-10">
            {currentTest?.questions.map(({ checked, correct, question_id: questionId }, index) => (
              <Tooltip
                key={questionId}
                title={getTooltipText(checked, correct)}
                // title={`${checked === correct ? 'Togri' : checked === 0 ? 'Belgilanmagan' : 'Xato'}`}
                arrow
              >
                <div
                  className={classNames('flex h-8 w-8 items-center justify-center rounded-full border', {
                    'border-green-500 text-green-500': checked === correct,
                    'border-red-500 text-red-500': checked !== 0 && checked !== correct,
                    'border-gray-400 text-gray-400': checked === 0
                  })}
                >
                  <span className="font-medium">{index + 1}</span>
                </div>
              </Tooltip>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center justify-center gap-3">
          <Link href={`${globalConst.pageNames.CHECK_TEST}/${ticketId}`}>
            <div className="flex flex-col items-center justify-center space-y-2 rounded-xl bg-gray-50 p-3">
              <WarningIcon />
              <p className="text-xs text-gray-400">{t('tekshirish')}</p>
            </div>
          </Link>
          <div
            onClick={() => setOpenRate(true)}
            className="flex cursor-pointer flex-col items-center justify-center space-y-2 rounded-xl bg-gray-50 p-3"
          >
            <TaskIcon />
            <p className="text-xs text-gray-400">{t('baholash')}</p>
          </div>
          <div
            onClick={() => setOpenShare(true)}
            className="flex cursor-pointer flex-col items-center justify-center space-y-2 rounded-xl bg-gray-50 p-3"
          >
            <Share2Icon />
            <p className="text-xs text-gray-400">{t('profile.ulashish')}</p>
          </div>
        </div>
      </Modal>

      <ApplicationModal
        ticketId={ticketId}
        open={openApplicationCreate}
        onClose={() => setOpenApplicationCreate(false)}
        score={myResults?.ball}
        passingScore={myResults?.direction?.passing_ball}
      />
      <RateModal ticketId={ticketId?.toString()} open={openRate} onClose={() => setOpenRate(false)} />
      <ShareModal
        title={t('test_natijalarini_ulashish')}
        shareUrl={`${globalConst.pageNames.RESULTS}/${ticketId}`}
        open={openShare}
        onClose={() => setOpenShare(false)}
      />
    </>
  );
};

export default ResultsModal;
